import React, { useState, useEffect, useCallback } from "react";
import { ACTIVE_API_BASE_URL } from "../config/apiConfig";
import apiClient from "../api/axiosClient";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/FormProduction.css";
import SidebarGuideline from "./SidebarGuideline";
import TextField from "@mui/material/TextField";
import {downloadFile} from '../common'

// Constants for max lengths
const FIELD_MAX_LENGTH = 100;
const COMPANY_PROFILE_MAX_LENGTH = 2400;
const COMMENT_MAX_LENGTH = 200;
const PHONE_MAX_LENGTH = 10;

// Validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

const RegistrationSP = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState(null);
  const awardTitle =
    location.state?.awardTitle || "Service Provider of the Year";

  const [userInfo, setUserInfo] = useState();
  const [formId, setFormId] = useState();
  const [PkId, setPkId] = useState(0);

  // Form state initialization
  const [formData, setFormData] = useState({
    Organisationname: "",
    category: "Service Provider of the Year",
    mailingAddress: "",
    authorityName: "",
    authorityTitle: "",
    authorityPhone: "",
    authorityEmail: "",
    applicant_name: "",
    applicant_phone: "",
    authorityLandline: "",
    applicant_email: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    companyProfile: "",
    awardJustification: "",
    approvingAuthoritySignature: null,
    declaration: false,
    comment: "",

    projectsValue2024: "",
    projectsValue2023: "",
    projectsAwarded2024: "",
    projectsAwarded2023: "",

    projectsInProgress: "", // [2024-25, 2023-24]
    projectsInProgressRemarks: "",
    projectsInProgressValue: "", // [2024-25, 2023-24]
    projectsCompleted: "", // [2024-25, 2023-24]
    projectsNoTimeOverrun: "", // [2024-25, 2023-24]
    projectsNoCostOverrun: "", // [2024-25, 2023-24]
    technologiesIntroduced: "", // [2024-25, 2023-24]
    technologiesValue: "", // [2024-25, 2023-24]
    fatalities: "", // [2024-25, 2023-24]
    lostTimeInjuries: "", // [2024-25, 2023-24]
    oshaIncidents: "", // [2024-25, 2023-24]
    manHoursOwn: "", // [2024-25, 2023-24]
    manHoursContract: "", // [2024-25, 2023-24]
    // New Projects (Board Approved)
    ongoingProject1Name: "",
    ongoingProject1Client: "",
    ongoingProject1Start: "",
    ongoingProject1End: "",
    ongoingProject1AwardValue: "",
    ongoingProject1ActualCost: "",
    ongoingProject2Name: "",
    ongoingProject2Client: "",
    ongoingProject2Start: "",
    ongoingProject2End: "",
    ongoingProject2AwardValue: "",
    ongoingProject2ActualCost: "",
    ongoingProject3Name: "",
    ongoingProject3Client: "",
    ongoingProject3Start: "",
    ongoingProject3End: "",
    ongoingProject3AwardValue: "",
    ongoingProject3ActualCost: "",
    ongoingProject4Name: "",
    ongoingProject4Client: "",
    ongoingProject4Start: "",
    ongoingProject4End: "",
    ongoingProject4AwardValue: "",
    ongoingProject4ActualCost: "",
    ongoingProject5Name: "",
    ongoingProject5Client: "",
    ongoingProject5Start: "",
    ongoingProject5End: "",
    ongoingProject5AwardValue: "",
    ongoingProject5ActualCost: "",
    // Quantitative fields from Part 2 (New Technologies)
    technology1Tech: "",
    technology1Project: "",
    technology1Client: "",
    technology1Cost: "",
    technology1Value: "",
    technology2Tech: "",
    technology2Project: "",
    technology2Client: "",
    technology2Cost: "",
    technology2Value: "",
    technology3Tech: "",
    technology3Project: "",
    technology3Client: "",
    technology3Cost: "",
    technology3Value: "",
    technology4Tech: "",
    technology4Project: "",
    technology4Client: "",
    technology4Cost: "",
    technology4Value: "",
    technology5Tech: "",
    technology5Project: "",
    technology5Client: "",
    technology5Cost: "",
    technology5Value: "",
    // Attachments
    attachments1: { description: "", file: null },
    attachments2: { description: "", file: null },
    attachments3: { description: "", file: null },
    attachments4: { description: "", file: null },
  });

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("user_info");
      if (stored) {
        const user = JSON.parse(stored);
        setUserInfo(JSON.parse(stored));
        setFormId(`RegistrationSP_${user.id}`);
        setFormData((prev) => ({
          ...prev,
          userid: user.id || "",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company_name: user.organisation_name || "",
          Organisationname: user.organisation_name || "", // prefill organisation_name field
          // Fill applicant_* only (we DON'T prefill contact_* here)
          applicant_name: user.applicant_name || "",
          applicant_phone: user.applicant_phone || "",
          applicant_email: user.email || "",
        }));
      }
    } catch (err) {
      console.warn("Failed to parse user_info from localStorage:", err);
    }
  }, []);

  // This use Effect Prefill user Data using api;
  useEffect(() => {
    if (userInfo && formId) {
      prefillFormData();
    } else {
      console.warn(
        `userInfo OR formId is invalid; Kindly check the method. and data`
      );
    }
  }, [userInfo, formId]);

  const isSubmited = () => formData.form_mode === "submit";

  function prefillFormData() {
    const url = `/service-provider/?userid=${userInfo.id}&form_id=${formId}`;
    apiClient
      .get(url)
      .then((res) => {
        if (!Array.isArray(res.data) || res.data.length !== 1) {
          console.log("No prefill data found:", res.data);
          return;
        }

        const _ResData = res.data[0];
        setPkId(_ResData.id || 0);

        // Build newFields immutably
        const newFields = {
          // Basic / text fields
          Organisationname:
            _ResData.organisation_name ??
            _ResData.organisationName ??
            _ResData.company_name ??
            "",
          category: _ResData.category ?? "Service Provider of the Year",
          mailingAddress: _ResData.mailing_address ?? "",
          authorityName: _ResData.authority_name ?? "",
          authorityTitle: _ResData.authority_title ?? "",
          authorityPhone: _ResData.authority_phone ?? "",
          authorityLandline:
            _ResData.authority_landline ?? _ResData.authorityLandline ?? "",
          authorityEmail: _ResData.authority_email ?? "",
          applicant_name:
            _ResData.applicant_name ?? _ResData.contact_name ?? "",
          applicant_phone:
            _ResData.applicant_phone ?? _ResData.contact_phone ?? "",
          applicant_email:
            _ResData.applicant_email ?? _ResData.contact_email ?? "",
          contact_name: _ResData.contact_name ?? "",
          contact_phone: _ResData.contact_phone ?? "",
          contact_email: _ResData.contact_email ?? "",
          companyProfile: _ResData.company_profile ?? "",
          awardJustification: _ResData.award_justification ?? "",
          comment: _ResData.comment ?? "",
          declaration:
            _ResData.declaration === true || _ResData.declaration === "true",
          form_mode: _ResData.form_mode ?? "draft",
        };

        // ---- Simple numeric / single-value fields (strings for controlled inputs) ----
        newFields.projectsValue2024 = _ResData.projects_value_2024 ?? "";
        newFields.projectsValue2023 = _ResData.projects_value_2023 ?? "";
        newFields.projectsAwarded2024 = _ResData.projects_awarded_2024 ?? "";
        newFields.projectsAwarded2023 = _ResData.projects_awarded_2023 ?? "";

        newFields.projectsInProgress = _ResData.projects_in_progress ?? "";
        newFields.projectsInProgressRemarks =
          _ResData.projects_in_progress_remarks ?? "";
        newFields.projectsInProgressValue =
          _ResData.projects_in_progress_value ?? "";

        newFields.projectsCompleted = _ResData.projects_completed ?? "";
        newFields.projectsNoTimeOverrun =
          _ResData.projects_no_time_overrun ?? "";
        newFields.projectsNoCostOverrun =
          _ResData.projects_no_cost_overrun ?? "";

        newFields.technologiesIntroduced =
          _ResData.technologies_introduced ?? "";
        newFields.technologiesValue = _ResData.technologies_value ?? "";

        newFields.fatalities = _ResData.fatalities ?? "";
        newFields.lostTimeInjuries = _ResData.lost_time_injuries ?? "";
        newFields.oshaIncidents = _ResData.osha_incidents ?? "";
        newFields.manHoursOwn = _ResData.man_hours_own ?? "";
        newFields.manHoursContract = _ResData.man_hours_contract ?? "";

        // ---- Ongoing Projects (1..5) ----
        for (let i = 1; i <= 5; i++) {
          newFields[`ongoingProject${i}Name`] =
            _ResData[`ongoing_project_${i}_name`] ?? "";
          newFields[`ongoingProject${i}Client`] =
            _ResData[`ongoing_project_${i}_client`] ?? "";
          // keep date strings (ISO or whatever you store) so inputs can display
          newFields[`ongoingProject${i}Start`] =
            _ResData[`ongoing_project_${i}_start`] ?? "";
          newFields[`ongoingProject${i}End`] =
            _ResData[`ongoing_project_${i}_end`] ?? "";
          newFields[`ongoingProject${i}AwardValue`] =
            _ResData[`ongoing_project_${i}_award_value`] ?? "";
          newFields[`ongoingProject${i}ActualCost`] =
            _ResData[`ongoing_project_${i}_actual_cost`] ?? "";
        }

        // ---- Technologies (1..5) ----
        for (let i = 1; i <= 5; i++) {
          newFields[`technology${i}Tech`] =
            _ResData[`technology_${i}_tech`] ?? "";
          newFields[`technology${i}Project`] =
            _ResData[`technology_${i}_project`] ?? "";
          newFields[`technology${i}Client`] =
            _ResData[`technology_${i}_client`] ?? "";
          newFields[`technology${i}Cost`] =
            _ResData[`technology_${i}_cost`] ?? "";
          newFields[`technology${i}Value`] =
            _ResData[`technology_${i}_value_contributed`] ?? "";
        }

        // ---- Attachments (1..4) ----
        // Keep file: null (no File object until user picks), store url for preview
        for (let n = 1; n <= 4; n++) {
          const fileKey = `attachments${n}`;
          const descKey = `${fileKey}_desc`;
          newFields[fileKey] = {
            description: _ResData[descKey] ?? "",
            file: null,
            url: _ResData[fileKey] ?? _ResData[`${fileKey}_url`] ?? null,
          };
        }

        // approving authority signature: file object stays null, store url for preview/storage check
        newFields.approvingAuthoritySignature = null;
        newFields.approvingAuthoritySignatureUrl =
          _ResData.approving_authority_file ??
          _ResData.approving_authority_file_url ??
          null;

        // Finally set the form state immutably so React picks up array/string changes
        setFormData((prev) => ({ ...prev, ...newFields }));

        console.log("Service-provider prefill applied, pkId:", _ResData.id);
      })
      .catch((err) => {
        console.error("prefillFormData error:", err);
      });
  }

  // UI State
  const [currentStep, setCurrentStep] = useState(1);
  const [copyApplicantData, setCopyApplicantData] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Validation helpers
  const validateEmail = (email) => emailRegex.test(email);
  const validatePhone = (phone) => phoneRegex.test(phone);

  // Clear error on field
  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (prev[field]) {
        const { [field]: omitted, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Handle changes for inputs/textareas/selects
  const handleChange = (name, value, index = null) => {
    if ([1, 2, 3, 4].includes(currentStep)) {
      let applicableMaxLength = FIELD_MAX_LENGTH;
      if (name === "companyProfile")
        applicableMaxLength = COMPANY_PROFILE_MAX_LENGTH;
      else if (
        [
          "comment",
          "project1_remarks",
          "project2_remarks",
          "project3_remarks",
          "project4_remarks",
          "project5_remarks",
        ].includes(name)
      ) {
        applicableMaxLength = 50;
      }

      if (typeof value === "string") {
        if (name === "companyProfile") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 300) {
            alert("Maximum 300 words allowed.");
            return;
          }
        } else if (name === "comment") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 200) {
            alert("Maximum 200 words allowed in Comments.");
            return;
          }
        } else if (value.length > applicableMaxLength) {
          alert(`Value cannot exceed ${applicableMaxLength} characters.`);
          return;
        }
      }
    }

    // Name validation
    if (
      [
        "Organisationname",
        "authorityName",
        "contactName",
        "project1_name",
        "project2_name",
        "project3_name",
        "project4_name",
        "project5_name",
      ].includes(name)
    ) {
      const isValid = /^[A-Za-z\s]*$/.test(value);
      if (!isValid && value !== "") {
        alert("Only letters and spaces are allowed.");
        return;
      }
    }

    // Phone validation
    if (["authorityPhone", "contact_phone"].includes(name)) {
      const numericValue = value.replace(/\D/g, "").slice(0, PHONE_MAX_LENGTH);
      if (numericValue.length > PHONE_MAX_LENGTH) {
        alert("Phone number must not exceed 10 digits.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      clearFieldError(name);
      return;
    }

    // Number field validation for quantitative fields and project capex
    if (
      [
        "projectsAwarded",
        "projectsValue",
        "projectsInProgress",
        "projectsInProgressValue",
        "projectsCompleted",
        "projectsNoTimeOverrun",
        "projectsNoCostOverrun",
        "technologiesIntroduced",
        "technologiesValue",
        "fatalities",
        "lostTimeInjuries",
        "oshaIncidents",
        "manHoursOwn",
        "manHoursContract",
        "project1_capex",
        "project2_capex",
        "project3_capex",
        "project4_capex",
        "project5_capex",
      ].includes(name)
    ) {
      if (index !== null || name.includes("capex")) {
        const val = value.toString();
        if (val.startsWith("-") || val.toLowerCase().includes("e")) {
          alert("Negative numbers or scientific notation are not allowed.");
          return;
        }
      }
    }

    // Handle array-based inputs for quantitative fields
    if (index !== null) {
      setFormData((prev) => {
        const updatedArray = Array.isArray(prev[name])
          ? [...prev[name]]
          : ["", ""];
        updatedArray[index] = value;
        return {
          ...prev,
          [name]: updatedArray,
        };
      });
      clearFieldError(`${name}[${index}]`);
    } else {
      // Default handling for non-array fields
      setFormData((prev) => ({
        ...prev,
        [name]: typeof value === "boolean" ? value : value || "",
      }));
      clearFieldError(name);
    }

    if (name === "Organisationname" && value && currentStep === 1) setError("");
    if (name === "mailingAddress" && value?.trim() && currentStep === 1)
      setError("");
    if (name === "authorityName" && value && currentStep === 2) setError("");
    if (name === "authorityTitle" && value && currentStep === 2) setError("");
    if (name === "authorityEmail" && value && currentStep === 2) setError("");
    if (name === "authorityPhone" && value && currentStep === 2) setError("");
  };

  // Handle checkbox for copying applicant data

  const handleCopyApplicantToggle = (e) => {
    const checked = e.target.checked;
    // setCopyApplicantData(checked);

    const model = {
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    };

    if (checked) {
      const userInfo = JSON.parse(sessionStorage.getItem("user_info"));
      if (userInfo) {
        model.contact_name = userInfo.applicant_name;
        model.contact_phone = userInfo.applicant_phone;
        model.contact_email = userInfo.email;
      }
    }

    setFormData((prev) => ({ ...prev, ...model }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
  };

  // Keep contact fields in sync if checkbox ON and applicant fields (or localStorage) change
  // This effect reads live values from localStorage to honor "pick data from localStorage" requirement.
  // useEffect(() => {
  //   if (!copyApplicantData) return;

  //   let user = {};
  //   try {
  //     const raw = localStorage.getItem("user_info");
  //     if (raw) user = JSON.parse(raw);
  //   } catch (err) {
  //     console.warn("Failed to parse user_info from localStorage in sync effect:", err);
  //   }

  //   setFormData((prev) => ({
  //     ...prev,
  //     contact_name: user.applicant_name || prev.applicant_name || "",
  //     contact_phone: user.applicant_phone || prev.applicant_phone || "",
  //     contact_email: user.email || prev.applicant_email || "",
  //   }));

  //   clearFieldError("contact_name");
  //   clearFieldError("contact_phone");
  //   clearFieldError("contact_email");
  //   // We intentionally do not add formData.* to the dependency array here that would cause infinite loops.
  //   // The dependency below ensures we re-run when copyApplicantData toggles.
  // }, [copyApplicantData, clearFieldError]);

  // Handle attachment changes
  const handleAttachmentChange = (key, field, value, event = null) => {
    if (field === "file" && value) {
      const file = value;
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for attachments.");
        if (event) event.target.value = null;
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for attachments.");
        if (event) event.target.value = null;
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
    clearFieldError(key);
  };

  // Handle approving authority signature upload
  const handleApprovingAuthorityChange = (files) => {
    const file = files[0];
    if (file) {
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for the signature.");
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for the signature.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        approvingAuthoritySignature: file,
      }));
      clearFieldError("approvingAuthoritySignature");
    }
  };

  const handlePrint = () => {
    const fullHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${awardTitle || "Form Submission"}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
          h1 { text-align: center; color: #1e40af; }
        </style>
      </head>
      <body>
        <h1>${awardTitle}</h1>

        <h2>Organization & Contact Details</h2>
        <p><strong>Organisation Name:</strong> ${
          formData.Organisationname || ""
        }</p>
        <p><strong>Postal Address:</strong> ${formData.mailingAddress || ""}</p>
        <p><strong>Approving Authority Name:</strong> ${
          formData.authorityName || ""
        }</p>
        <p><strong>Approving Authority Designation:</strong> ${
          formData.authorityTitle || ""
        }</p>
        <p><strong>Approving Authority Landline:</strong> ${
          formData.authorityLandline || ""
        }</p>
        <p><strong>Approving Authority Phone:</strong> ${
          formData.authorityPhone || ""
        }</p>
        <p><strong>Approving Authority Email:</strong> ${
          formData.authorityEmail || ""
        }</p>

        <h2>Nodal Official Contact Details:</h2>
        <p><strong>Contact Name:</strong> ${formData.contact_name || ""}</p>
        <p><strong>Contact Phone:</strong> ${formData.contact_phone || ""}</p>
        <p><strong>Contact Email:</strong> ${formData.contact_email || ""}</p>
        <p><strong>Brief write up on company profile and the activities specific to service provided in the year 2024-25:</strong> ${
          formData.companyProfile || ""
        }</p>

        <h2>Quantitative Information - Part 1: Project Metrics & Safety</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Particulars</th>
              <th>2024-25</th>
              <th>2023-24</th>
            </tr>
          </thead>
          <tbody>
            ${part1
              .slice(0, 2)
              .map(
                ([num, title, key2024, key2023]) => `
              <tr>
                <td>${num}</td>
                <td>${title}</td>
                <td>${formData[key2024] || ""}</td>
                <td>${formData[key2023] || ""}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Particulars</th>
              <th>2024-25</th>
            </tr>
          </thead>
          <tbody>
            ${part1
              .slice(2)
              .map(
                ([num, title, key2024]) => `
              <tr>
                <td>${num}</td>
                <td>${title}</td>
                <td>${formData[key2024] || ""}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <h2>Quantitative Information - Part 2: Ongoing Projects Information</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Project Name</th>
              <th>Client Name</th>
              <th>Start Date</th>
              <th>Scheduled Completion Date</th>
              <th>Awarded Value (INR Crore)</th>
              <th>Actual Cost (INR Crore)</th>
            </tr>
          </thead>
          <tbody>
            ${ongoingProjects
              .map(
                ([num, name, client, start, end, awardValue, actualCost]) => `
              <tr>
                <td>${num}</td>
                <td>${formData[name] || ""}</td>
                <td>${formData[client] || ""}</td>
                <td>${formData[start] || ""}</td>
                <td>${formData[end] || ""}</td>
                <td>${formData[awardValue] || ""}</td>
                <td>${formData[actualCost] || ""}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <h2>Quantitative Information - Part 2: List of New Technologies</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name of the Technology</th>
              <th>Project Name</th>
              <th>Client Name</th>
              <th>Cost (INR Crore)</th>
              <th>Value Contributed (100 words)</th>
            </tr>
          </thead>
          <tbody>
            ${newTechnologies
              .map(
                ([num, tech, project, client, cost, value]) => `
              <tr>
                <td>${num}</td>
                <td>${formData[tech] || ""}</td>
                <td>${formData[project] || ""}</td>
                <td>${formData[client] || ""}</td>
                <td>${formData[cost] || ""}</td>
                <td>${formData[value] || ""}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
         <h2>Attachments</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;width:10px">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Description</th>
            <th style="border: 1px solid #000; padding: 8px;">File Name</th>
          </tr>
        </thead>
        <tbody>
          ${[1, 2, 3, 4]
            .map(
              (i) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${i}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].description || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].file?.name || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>



        <h2>Comments</h2>
        <p>${formData.comment || ""}</p>

        <h2>Declaration</h2>
        <p>I declare that the information submitted is true and complete.</p>
        <br/>
        <p><strong>Approving Authority Name:</strong> ${
          formData.authorityName || ""
        }</p>
        <p><strong>Approving Authority Designation:</strong> ${
          formData.authorityTitle || ""
        }</p>
        <p><strong>Signed on:</strong> ${
          formData.signatureDate || new Date().toLocaleDateString()
        }</p>
        <p><strong>Approving Authority Signature:</strong></p>
      </body>
    </html>
  `;

    const printWindow = window.open("", "_blank", "height=800,width=1000");
    printWindow.document.write(fullHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Navigation handlers
  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.mailingAddress.trim()) {
        alert("Mailing address is required.");
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.authorityName) {
        alert("Authority name is required.");
        return;
      }
      if (!formData.authorityTitle) {
        alert("Authority designation is required.");
        return;
      }
      if (!formData.authorityEmail || !validateEmail(formData.authorityEmail)) {
        alert("Please enter a valid Authority email.");
        return;
      }
      // if (!formData.authorityPhone || !validatePhone(formData.authorityPhone)) {
      //   alert('Authority phone must be exactly 10 digits.');
      //   return;
      // }
      if (!formData.authorityLandline) {
        alert("Authority Landline is required.");
        return;
      }
      if (!formData.contact_name) {
        alert("Contact name is required.");
        return;
      }
      if (!formData.companyProfile) {
        alert("Company Profile is required.");
        return;
      }
      if (formData.contact_email && !validateEmail(formData.contact_email)) {
        alert("Please enter a valid Contact email.");
        return;
      }
      if (
        formData.contact_phone &&
        !validatePhone(formData.contact_phone) &&
        !copyApplicantData
      ) {
        alert("Contact phone must be exactly 10 digits.");
        return;
      }
    }
    if (currentStep === 3 && hasEmptyFieldsStep3()) {
      if (!window.confirm("Data not entered,If you wish to continue?")) {
        return;
      }
    }

    if (currentStep === 4 && hasEmptyFieldsStep4()) {
      if (!window.confirm("Data not entered,If you wish to continue?")) {
        return;
      }
    }

    setError("");
    if (currentStep < 5) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  // Save draft handler
  const handleSaveDraft = () => {
    localStorage.setItem("registrationSPDraft", JSON.stringify({ formData }));
    handleSubmit(null);
    alert("Draft Saved!");
  };

  const finalSubmit = () => {
    handleSubmit(null, "submited");
  };

  // Submit form handler
  // Replace your existing handleSubmit with this
  async function handleSubmit(event, clickType = "draft") {
    if (event) {
      event?.preventDefault();
    }
    console.log(
      "############################ Save the DAta on Server ##########################"
    );
    // 1) Make a local copy of formData so we don't rely on async state updates
    let payload = { ...formData };

    // Pull user info from localStorage
    try {
      const raw = localStorage.getItem("user_info");
      if (raw) {
        const user = JSON.parse(raw);
        payload.userid = user.id ?? payload.userid;
        payload.firstname = user.first_name ?? payload.firstname;
        payload.lastname = user.last_name ?? payload.lastname;
        payload.company_name = user.organisation_name ?? payload.company_name;
        payload.Organisationname =
          user.organisation_name ?? payload.Organisationname;
      }
    } catch (err) {
      console.warn("Failed to load user_info:", err);
    }

    // Basic validations

    // Helpers
    const clean = (v) =>
      v === null || v === undefined ? "" : String(v).replace(/,/g, "").trim();
    const isISODate = (s) =>
      /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(new Date(s).getTime());
    const parseDecimal = (s) => {
      if (s === "" || s === null || s === undefined) return null;
      const n = Number(clean(s));
      return Number.isFinite(n) ? n : null;
    };
    const parseIntSafe = (s) => {
      if (s === "" || s === null || s === undefined) return null;
      const n = Number(clean(s));
      if (!Number.isFinite(n)) return null;
      if (!Number.isInteger(n)) {
        if (Math.abs(n - Math.round(n)) < 1e-9) return Math.round(n);
        return null;
      }
      return Math.round(n);
    };

    const parsed = {};

    // ---- Part 1 Quantitative ----
    parsed["projects_awarded_2024"] = parseIntSafe(payload.projectsAwarded2024);
    parsed["projects_awarded_2023"] = parseIntSafe(payload.projectsAwarded2023);
    parsed["projects_value_2024"] = parseDecimal(payload.projectsValue2024);
    parsed["projects_value_2023"] = parseDecimal(payload.projectsValue2023);

    parsed["projects_in_progress"] = parseIntSafe(payload.projectsInProgress);
    parsed["projects_in_progress_remarks"] =
      payload.projectsInProgressRemarks || "";
    parsed["projects_in_progress_value"] = parseDecimal(
      payload.projectsInProgressValue
    );

    parsed["projects_completed"] = parseIntSafe(payload.projectsCompleted);
    parsed["projects_no_time_overrun"] = parseIntSafe(
      payload.projectsNoTimeOverrun
    );
    parsed["projects_no_cost_overrun"] = parseIntSafe(
      payload.projectsNoCostOverrun
    );

    parsed["technologies_introduced"] = parseIntSafe(
      payload.technologiesIntroduced
    );
    parsed["technologies_value"] = parseDecimal(payload.technologiesValue);

    parsed["fatalities"] = parseIntSafe(payload.fatalities);
    parsed["lost_time_injuries"] = parseIntSafe(payload.lostTimeInjuries);
    parsed["osha_incidents"] = parseIntSafe(payload.oshaIncidents);
    parsed["man_hours_own"] = parseIntSafe(payload.manHoursOwn);
    parsed["man_hours_contract"] = parseIntSafe(payload.manHoursContract);

    // ---- Ongoing Projects ----
    for (let i = 1; i <= 5; i++) {
      parsed[`ongoing_project_${i}_name`] =
        payload[`ongoingProject${i}Name`] || "";
      parsed[`ongoing_project_${i}_client`] =
        payload[`ongoingProject${i}Client`] || "";

      const start = clean(payload[`ongoingProject${i}Start`]);
      parsed[`ongoing_project_${i}_start`] =
        start && isISODate(start) ? start : null;

      const end = clean(payload[`ongoingProject${i}End`]);
      parsed[`ongoing_project_${i}_end`] = end && isISODate(end) ? end : null;

      parsed[`ongoing_project_${i}_award_value`] = parseDecimal(
        payload[`ongoingProject${i}AwardValue`]
      );
      parsed[`ongoing_project_${i}_actual_cost`] = parseDecimal(
        payload[`ongoingProject${i}ActualCost`]
      );
    }

    // ---- Technologies ----
    for (let i = 1; i <= 5; i++) {
      parsed[`technology_${i}_tech`] = payload[`technology${i}Tech`] || "";
      parsed[`technology_${i}_project`] =
        payload[`technology${i}Project`] || "";
      parsed[`technology_${i}_client`] = payload[`technology${i}Client`] || "";
      parsed[`technology_${i}_cost`] = parseDecimal(
        payload[`technology${i}Cost`]
      );
      parsed[`technology_${i}_value_contributed`] =
        payload[`technology${i}Value`];
    }

    // ---- Build FormData ----
    const fd = new FormData();

    fd.append("organisation_name", payload.Organisationname || "");
    fd.append("category", payload.category || "");
    fd.append("firstname", payload.firstname || "");
    fd.append("lastname", payload.lastname || "");
    fd.append("userid", payload.userid || "");
    fd.append("company_name", payload.company_name || "");
    fd.append("mailing_address", payload.mailingAddress || "");

    fd.append("authority_name", payload.authorityName || "");
    fd.append("authority_title", payload.authorityTitle || "");
    fd.append("authority_phone", payload.authorityPhone || "");
    fd.append("authorityLandline", formData.authorityLandline || "");
    fd.append("authority_email", payload.authorityEmail || "");

    fd.append("contact_name", payload.contact_name || "");
    fd.append("contact_phone", payload.contact_phone || "");
    fd.append("contact_email", payload.contact_email || "");

    fd.append("company_profile", payload.companyProfile || "");
    fd.append("award_justification", payload.awardJustification || "");
    fd.append("comment", payload.comment || "");
    fd.append("declaration", payload.declaration ? "true" : "false");

    fd.append("form_id", formId);
    fd.append("form_mode", clickType);
    // Add Table Pk Id;
    if (PkId > 0) fd.append("id", PkId);

    if (payload.approvingAuthoritySignature instanceof File) {
      fd.append(
        "approving_authority_file",
        payload.approvingAuthoritySignature
      );
    }

    Object.entries(parsed).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        fd.append(key, String(value));
      }
    });

    [1, 2, 3, 4].forEach((n) => {
      const slot = payload[`attachments${n}`] || {};
      if (slot.description) fd.append(`attachments${n}_desc`, slot.description);
      if (slot.file instanceof File) fd.append(`attachments${n}`, slot.file);
    });

    try {
      await apiClient.post("/service-provider/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      prefillFormData();
    } catch (err) {
      console.error("Submission error:", err);

      // 🔹 Check if backend says org already submitted
      if (err.response?.data?.organisation_name) {
        alert(err.response.data.organisation_name);
      } else {
        alert(
          "Submission failed: " +
            JSON.stringify(err.response?.data || err.message)
        );
      }
    }
  }

  // Quantitative data for steps 3 and 4
  const part1 = [
    [
      "1",
      "No. of Projects awarded",
      "projectsAwarded2024",
      "projectsAwarded2023",
    ],
    [
      "2",
      "Value of Projects awarded",
      "projectsValue2024",
      "projectsValue2023",
    ],
    ["3", "No. of Projects in Progress", "projectsInProgress"],
    [
      "4",
      "Total Value of Projects in Progress (INR Crore)",
      "projectsInProgressValue",
    ],
    ["5", "No. of Projects completed", "projectsCompleted"],
    [
      "6",
      "No. of Projects Completed without time overrun",
      "projectsNoTimeOverrun",
    ],
    [
      "7",
      "No. of Projects Completed without cost overrun",
      "projectsNoCostOverrun",
    ],
    ["8", "Number of new technologies introduced", "technologiesIntroduced"],
    [
      "9",
      "Value of the new technologies introduced (INR Crore)",
      "technologiesValue",
    ],
    ["10", "No. of Fatalities", "fatalities"],
    ["11", "Number of lost time injuries", "lostTimeInjuries"],
    ["12", "Number of OSHA recordable incidents", "oshaIncidents"],
    ["13", "Total Man Hours Worked (Own Employees)", "manHoursOwn"],
    [
      "14",
      "Total Man Hours Worked (Contractual Employees)",
      "manHoursContract",
    ],
  ];
  const ongoingProjects = [
    [
      "1",
      "ongoingProject1Name",
      "ongoingProject1Client",
      "ongoingProject1Start",
      "ongoingProject1End",
      "ongoingProject1AwardValue",
      "ongoingProject1ActualCost",
    ],
    [
      "2",
      "ongoingProject2Name",
      "ongoingProject2Client",
      "ongoingProject2Start",
      "ongoingProject2End",
      "ongoingProject2AwardValue",
      "ongoingProject2ActualCost",
    ],
    [
      "3",
      "ongoingProject3Name",
      "ongoingProject3Client",
      "ongoingProject3Start",
      "ongoingProject3End",
      "ongoingProject3AwardValue",
      "ongoingProject3ActualCost",
    ],
    [
      "4",
      "ongoingProject4Name",
      "ongoingProject4Client",
      "ongoingProject4Start",
      "ongoingProject4End",
      "ongoingProject4AwardValue",
      "ongoingProject4ActualCost",
    ],
    [
      "5",
      "ongoingProject5Name",
      "ongoingProject5Client",
      "ongoingProject5Start",
      "ongoingProject5End",
      "ongoingProject5AwardValue",
      "ongoingProject5ActualCost",
    ],
  ];

  const newTechnologies = [
    [
      "1",
      "technology1Tech",
      "technology1Project",
      "technology1Client",
      "technology1Cost",
      "technology1Value",
    ],
    [
      "2",
      "technology2Tech",
      "technology2Project",
      "technology2Client",
      "technology2Cost",
      "technology2Value",
    ],
    [
      "3",
      "technology3Tech",
      "technology3Project",
      "technology3Client",
      "technology3Cost",
      "technology3Value",
    ],
    [
      "4",
      "technology4Tech",
      "technology4Project",
      "technology4Client",
      "technology4Cost",
      "technology4Value",
    ],
    [
      "5",
      "technology5Tech",
      "technology5Project",
      "technology5Client",
      "technology5Cost",
      "technology5Value",
    ],
  ];

  const hasEmptyFieldsStep3 = () => {
    for (const section of part1) {
      const [num, label, key] = section;

      // Skip if no key (like headings or labels)
      if (!key) continue;

      const val = formData[key];

      // ✅ If it's undefined, null, or empty string, it's empty
      if (val === undefined || val === null || val === "") {
        return true;
      }

      // ✅ If it's an array, check each element
      if (
        Array.isArray(val) &&
        val.some((v) => v === "" || v === null || v === undefined)
      ) {
        return true;
      }
    }
    return false;
  };

  const hasEmptyFieldsStep4 = () => {
    // ✅ Check Ongoing Projects table
    for (const [
      num,
      name,
      client,
      start,
      end,
      awardValue,
      actualCost,
    ] of ongoingProjects) {
      const fields = [name, client, start, end, awardValue, actualCost];

      for (const field of fields) {
        const val = formData[field];

        if (val === undefined || val === null || val === "") {
          return true;
        }

        if (
          Array.isArray(val) &&
          val.some((v) => v === "" || v === null || v === undefined)
        ) {
          return true;
        }
      }
    }

    // ✅ Check New Technologies table
    for (const [num, tech, project, client, cost, value] of newTechnologies) {
      const fields = [tech, project, client, cost, value];

      for (const field of fields) {
        const val = formData[field];

        if (val === undefined || val === null || val === "") {
          return true;
        }

        if (
          Array.isArray(val) &&
          val.some((v) => v === "" || v === null || v === undefined)
        ) {
          return true;
        }
      }
    }

    return false; // ✅ All filled
  };
   const viewFile =  async (serverPath) => {
    // "https://award-backend.fipi.org.in/media/
    const extractPath = serverPath.split('/').filter(
  (val) =>
    val !== "" && // remove empty strings
    val !== "http:" &&
    val !== "https:" &&
    val !== "media" &&
    !val.startsWith("award") &&
    !val.startsWith("localhost")
).join('/');
    
    downloadFile(extractPath)
  }

  // Rendering steps content
  const renderStepContent = () => {
    const progress = ((currentStep - 1) / 4) * 100;

    return (
      <div
        className="form-step"
        role="form"
        aria-label={`Step ${currentStep} of 5`}
      >
        <div className="progress-bar" aria-hidden="true">
          <div className="progress" style={{ width: `${progress}%` }} />
        </div>

        {currentStep === 1 && (
          <>
            <h3 className="step-title">Step 1: Organization Details</h3>
            <div className="form-group">
              <label htmlFor="Organisationname">
                Organisation Name:
                {/* <span aria-hidden="true" className="text-red">*</span> */}
              </label>
              <input
                id="Organisationname"
                name="Organisationname"
                type="text"
                maxLength={FIELD_MAX_LENGTH}
                value={formData.Organisationname}
                // onChange={(e) => handleChange(e.target.name, e.target.value)}
                disabled={true} // Disable input as per requirement
                aria-describedby="Organisationname-error"
                className={`form-input ${
                  !formData.Organisationname && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
                placeholder="Enter organisation name"
                required
              />
              {fieldErrors.Organisationname && (
                <span
                  className="error-tooltip"
                  id="Organisationname-error"
                  role="alert"
                >
                  {fieldErrors.Organisationname}
                </span>
              )}
              {!formData.Organisationname && currentStep === 1 && (
                <span
                  className="error-tooltip"
                  id="Organisationname-error"
                  role="alert"
                >
                  Organisation name is required
                </span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="mailingAddress">
                Postal Address:{" "}
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <textarea
                id="mailingAddress"
                name="mailingAddress"
                value={formData.mailingAddress}
                rows={3}
                maxLength={FIELD_MAX_LENGTH}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                aria-describedby="mailingAddress-error"
                placeholder="Enter postal address"
                className={`form-textarea ${
                  !formData.mailingAddress.trim() && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
                required
              />
              {fieldErrors.mailingAddress && (
                <span
                  className="error-tooltip"
                  id="mailingAddress-error"
                  role="alert"
                >
                  {fieldErrors.mailingAddress}
                </span>
              )}
              {!formData.mailingAddress.trim() && currentStep === 1 && (
                <span
                  className="error-tooltip"
                  id="mailingAddress-error"
                  role="alert"
                >
                  Mailing address is required
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <h3 className="step-title">
              Step 2: Approving Authority & Contact
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section
                aria-labelledby="approving-authority-heading"
                className="step-section"
              >
                <h4 id="approving-authority-heading">Approving Authority</h4>
                <p className="note">
                  Approving authority should be concerned Director /Board level
                  executive.{" "}
                </p>
                <div className="form-group">
                  <label htmlFor="authorityName">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityName"
                    name="authorityName"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityName}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    aria-describedby="authorityName-error"
                    placeholder="Name"
                    className={`form-input ${
                      !formData.authorityName && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.authorityName && (
                    <span
                      className="error-tooltip"
                      id="authorityName-error"
                      role="alert"
                    >
                      {fieldErrors.authorityName}
                    </span>
                  )}
                  {!formData.authorityName && currentStep === 2 && (
                    <span
                      className="error-tooltip"
                      id="authorityName-error"
                      role="alert"
                    >
                      Name is required
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityTitle">
                    Designation:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityTitle"
                    name="authorityTitle"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityTitle}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    aria-describedby="authorityTitle-error"
                    placeholder="Designation"
                    className={`form-input ${
                      !formData.authorityTitle && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.authorityTitle && (
                    <span
                      className="error-tooltip"
                      id="authorityTitle-error"
                      role="alert"
                    >
                      {fieldErrors.authorityTitle}
                    </span>
                  )}
                  {!formData.authorityTitle && currentStep === 2 && (
                    <span
                      className="error-tooltip"
                      id="authorityTitle-error"
                      role="alert"
                    >
                      Designation is required
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Landline:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityLandline"
                    type="number"
                    name="authorityLandline"
                    value={formData.authorityLandline}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    // onBlur={(e) => handleBlur('authorityLandline', e.target.value)}
                    className={`form-input ${
                      !formData.authorityLandline && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Landline number"
                    aria-required="true"
                    aria-describedby={
                      fieldErrors.authorityLandline
                        ? "authorityLandline-error"
                        : undefined
                    }
                  />
                  {!formData.authorityLandline && currentStep === 2 && (
                    <span className="error-tooltip">
                      Authority Landline Number is required
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Mobile:{" "}
                    {/* <span className="text-red" aria-hidden="true"></span> */}
                  </label>
                  <input
                    id="authorityPhone"
                    type="tel"
                    name="authorityPhone"
                    value={formData.authorityPhone}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    // onBlur={(e) => handleBlur('authorityPhone', e.target.value)}
                    className={`form-input`}
                    placeholder="Phone number"
                    maxLength={PHONE_MAX_LENGTH}
                    aria-required="true"
                    // aria-describedby={fieldErrors.authorityPhone ? 'authorityPhone-error' : undefined}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="authorityEmail">
                    Email:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityEmail"
                    name="authorityEmail"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityEmail}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    aria-describedby="authorityEmail-error"
                    placeholder="Approving authority's email"
                    className={`form-input ${
                      !formData.authorityEmail && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.authorityEmail && (
                    <span
                      className="error-tooltip"
                      id="authorityEmail-error"
                      role="alert"
                    >
                      {fieldErrors.authorityEmail}
                    </span>
                  )}
                  {!formData.authorityEmail && currentStep === 2 && (
                    <span
                      className="error-tooltip"
                      id="authorityEmail-error"
                      role="alert"
                    >
                      Email is required
                    </span>
                  )}
                </div>
              </section>
              <section
                aria-labelledby="contacts-heading"
                className="step-section"
              >
                <h4 id="contacts-heading">
                  Contacts (Nodal Officials){" "}
                  <span aria-hidden="true" className="text-red">
                    *
                  </span>
                </h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="copyApplicantData"
                      // checked={copyApplicantData}
                      onChange={handleCopyApplicantToggle}
                      className="form-checkbox"
                    />{" "}
                    Same as applicant
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_name">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_name}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    placeholder="Contact name"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      !formData.contact_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    aria-describedby="contact_name-error"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_phone">
                    Phone:
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formData.contact_phone}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    aria-describedby="contact_phone-error"
                    disabled={copyApplicantData}
                    placeholder="10-digit phone number"
                    className={`form-input ${
                      !formData.contact_phone && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_email">Email</label>
                  <input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_email}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    placeholder="Contact email"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      fieldErrors.contact_email ? "has-error" : ""
                    }`}
                    aria-describedby="contact_email-error"
                  />
                </div>
              </section>
            </div>
            <div className="form-group">
              <label htmlFor="companyProfile">
                Brief write up on company profile and the activities specific to
                service provided in the year 2024-25.
              </label>
              <p className="note">(within 300 words)</p>
              <textarea
                id="companyProfile"
                name="companyProfile"
                value={formData.companyProfile}
                rows={6}
                // maxLength={COMPANY_PROFILE_MAX_LENGTH}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                className="form-textarea"
                placeholder="Enter company profile"
                aria-describedby="companyProfile-error"
              />
              {fieldErrors.companyProfile && (
                <span
                  className="error-tooltip"
                  id="companyProfile-error"
                  role="alert"
                >
                  {fieldErrors.companyProfile}
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <h3 className="step-title">
              Step 3: Quantitative Information Part 1
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024-25</th>
                  <th>2023-24</th>
                </tr>
              </thead>
              <tbody>
                {part1.slice(0, 2).map(([num, title, key2024, key2023]) => (
                  <tr key={num}>
                    <td className="sno-cell">{num}</td>
                    <td className="label-cell">{title}</td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={key2024}
                        value={formData[key2024] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange(e.target.name, val);
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                        className="form-input"
                        inputProps={{
                          inputMode: "decimal", // numeric keypad for mobile
                          pattern: "[0-9]*\\.?[0-9]*", // digits + optional decimal
                          min: 0,
                        }}
                      />
                    </td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={key2023}
                        value={formData[key2023] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange(e.target.name, val);
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                        className="form-input"
                        inputProps={{
                          inputMode: "decimal", // numeric keypad for mobile
                          pattern: "[0-9]*\\.?[0-9]*", // digits + optional decimal
                          min: 0,
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024-25</th>
                </tr>
              </thead>
              <tbody>
                {part1.slice(2).map(([num, title, key2024]) => (
                  <tr key={num}>
                    <td className="sno-cell">{num}</td>
                    <td className="label-cell">{title}</td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={key2024}
                        value={formData[key2024] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange(e.target.name, val);
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                        className="form-input"
                        inputProps={{
                          inputMode: "decimal", // numeric keypad for mobile
                          pattern: "[0-9]*\\.?[0-9]*", // digits + optional decimal
                          min: 0,
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {currentStep === 4 && (
          <>
            <h3 className="step-title">
              Step 4: Quantitative Information Part 2
            </h3>
            <table className="quant-table">
              <caption className="table-header">
                Table 1: Ongoing Projects Information
              </caption>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Project Name</th>
                  <th>Client Name</th>
                  <th>Start Date</th>
                  <th>Scheduled Completion Date</th>
                  <th>Awarded Value (INR Crore)</th>
                  <th>Actual Cost (INR Crore)</th>
                </tr>
              </thead>
              <tbody>
                {ongoingProjects.map(
                  ([num, name, client, start, end, awardValue, actualCost]) => (
                    <tr key={num}>
                      <td className="sno-cell">{num}</td>
                      <td>
                        <input
                          type="text"
                          name={name}
                          value={formData[name] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          maxLength={FIELD_MAX_LENGTH}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={client}
                          value={formData[client] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          maxLength={FIELD_MAX_LENGTH}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          name={start}
                          value={formData[start] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          name={end}
                          value={formData[end] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={awardValue}
                          value={formData[awardValue] || ""}
                          onChange={(e) => {
                            // Allow only digits
                            if (/^\d*$/.test(e.target.value)) {
                              handleChange(e.target.name, e.target.value);
                            }
                          }}
                          className="form-input"
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={actualCost}
                          value={formData[actualCost] || ""}
                          onChange={(e) => {
                            // Allow only digits
                            if (/^\d*$/.test(e.target.value)) {
                              handleChange(e.target.name, e.target.value);
                            }
                          }}
                          className="form-input"
                          min="0"
                        />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            <br />
            <table className="quant-table">
              <caption className="table-header">
                Table 2: List of New Technologies
              </caption>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Name of the Technology</th>
                  <th>Project Name</th>
                  <th>Client Name</th>
                  <th>Cost (INR Crore)</th>
                  <th>Value Contributed (100 words)</th>
                </tr>
              </thead>
              <tbody>
                {newTechnologies.map(
                  ([num, tech, project, client, cost, value]) => (
                    <tr key={num}>
                      <td className="sno-cell">{num}</td>
                      <td>
                        <input
                          type="text"
                          name={tech}
                          value={formData[tech] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          maxLength={FIELD_MAX_LENGTH}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={project}
                          value={formData[project] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          maxLength={FIELD_MAX_LENGTH}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={client}
                          value={formData[client] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          maxLength={FIELD_MAX_LENGTH}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name={cost}
                          value={formData[cost] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={value}
                          value={formData[value] || ""}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          className="form-input"
                          maxLength={FIELD_MAX_LENGTH}
                        />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>

            <div className="form-group">
              <label htmlFor="comment">Comments</label>
              <textarea
                id="comment"
                name="comment"
                // maxLength={COMMENT_MAX_LENGTH}
                value={formData.comment}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                className="form-textarea"
                placeholder="Comments in (200 words) against input parameter, if any"
                rows={4}
              />
              {fieldErrors.comment && (
                <span className="error-tooltip" id="comment-error" role="alert">
                  {fieldErrors.comment}
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 5 && (
          <>
            <h3 className="step-title">Step 5: Attachments & Declaration</h3>
            <div className="form-group">
              <label>List of Attachments (Optional):</label>
              <table className="quant-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Description</th>
                    <th>Upload (jpg, png, pdf; max 5 MB)</th>
                  </tr>
                </thead>
                {/* <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key];
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                            aria-describedby={`${key}-description-error`}
                          />
                          {fieldErrors[`${key}.description`] && (
                            <span
                              className="error-tooltip"
                              id={`${key}-description-error`}
                              role="alert"
                            >
                              {fieldErrors[`${key}.description`]}
                            </span>
                          )}
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                            aria-describedby={`${key}-file-error`}
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                          {fieldErrors[`${key}.file`] && (
                            <span
                              className="error-tooltip"
                              id={`${key}-file-error`}
                              role="alert"
                            >
                              {fieldErrors[`${key}.file`]}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody> */}
                <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key] || {
                      description: "",
                      file: null,
                    };
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                          {attachment.url && !attachment.file && (
                            <p className="file-name">
                              Uploaded file:{" "}
                              <button
                                type="button"
                                onClick={() => viewFile(attachment.url)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#06c",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                View
                              </button>
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="form-group">
              <label>
                Print the completed application form and upload it with the
                signature of the approving authority. Without signature of the
                approving authority, application will not be considered valid.
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline"
                >
                  Print Preview
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="approvingAuthoritySignature">
                Upload Document with Approving Authority Signature
                (Director/Board Level)
                <span aria-hidden="true" className="text-red">
                  *
                </span>
                :
              </label>
              <input
                type="file"
                id="approvingAuthoritySignature"
                accept=".jpg,.png,.pdf"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files.length > 0) {
                    const confirmed = window.confirm(
                      "Are you sure the form is approved and signed by approving authority?"
                    );
                    if (!confirmed) {
                      // If user clicks "Cancel", clear the input
                      e.target.value = "";
                      return;
                    }
                    handleApprovingAuthorityChange(files);
                  }
                }}
                className={`form-input mt-4 ${
                  fieldErrors.approvingAuthoritySignature ? "has-error" : ""
                }`}
                aria-describedby="approvingAuthoritySignature-error"
                required
              />
              {formData.approvingAuthoritySignature && (
                <p className="file-name">
                  Selected file: {formData.approvingAuthoritySignature.name}
                </p>
              )}
              {formData.approvingAuthoritySignatureUrl &&
                !formData.approvingAuthoritySignature && (
                  <p className="file-name">
                    Uploaded file:{" "}
                    <button
                      type="button"
                      onClick={() =>
                        viewFile(formData.approvingAuthoritySignatureUrl)
                      }
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#06c",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  </p>
                )}
              {fieldErrors.approvingAuthoritySignature && (
                <span
                  className="error-tooltip"
                  id="approvingAuthoritySignature-error"
                  role="alert"
                >
                  {fieldErrors.approvingAuthoritySignature}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  id="declaration"
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={(e) =>
                    handleChange(e.target.name, e.target.checked)
                  }
                  className={`form-checkbox ${
                    fieldErrors.declaration ? "has-error" : ""
                  }`}
                  aria-describedby="declaration-error"
                  required
                />{" "}
                I declare that the information submitted is true and complete.
              </label>
              {fieldErrors.declaration && (
                <span
                  className="error-tooltip"
                  id="declaration-error"
                  role="alert"
                >
                  {fieldErrors.declaration}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };
  useEffect(() => {
    const middlePosition = window.innerHeight / 2; // middle
    window.scrollTo({ top: middlePosition + 100, behavior: "smooth" });
  }, [currentStep]);

  return (
    <div className="sidebar-guideline-container">
      <SidebarGuideline
        isOpen={true}
        sidebarItems={[{ id: "Guideline", label: "Guideline" }]}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        selectedAwardCategory={formData.category}
      />
      <div className="application-form">
        <div className="form-header">
          <h1>{awardTitle}</h1>
          <h6>Step {currentStep} of 5</h6>
        </div>
        {error && <div className="error">{error}</div>}
        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your submission!</h2>
            <p>Your Application has been successfully submitted.</p>
            <button onClick={() => navigate("/#Award%20Categories")}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            {currentStep === 1 && (
              <div className="form-navigation-step1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
            {currentStep > 1 && (
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                {currentStep < 5 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                )}
                {currentStep === 5 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isSubmited()}
                    onClick={(e) => {
                      if (!formData.declaration) {
                        return alert(
                          "Please accept the declaration before submitting."
                        );
                      }
                      if (!formData.approvingAuthoritySignature) {
                        return alert(
                          "Please upload the approving authority signature file."
                        );
                      }
                      if (!window.confirm("Are you sure you want to submit?")) {
                        e.preventDefault();
                      }
                      alert(
                        "Service provider registration submitted successfully!"
                      );

                      setIsSubmitted(true);

                      handleSubmit(e, "submit");
                    }}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationSP;
